import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import {
  softwareUpdatePackageService,
  type Package,
  type PackageDefinition,
  type CreatePackageRequest,
  type UpdatePackageRequest,
  type PackageRolloutsResponse,
} from '@/lib/software-update-package.service';

export const softwareUpdatePackageKeys = {
  all: ['software-update-packages'] as const,
  lists: () => [...softwareUpdatePackageKeys.all, 'list'] as const,
  list: () => [...softwareUpdatePackageKeys.lists()] as const,
  details: () => [...softwareUpdatePackageKeys.all, 'detail'] as const,
  detail: (id: string) => [...softwareUpdatePackageKeys.details(), id] as const,
  definitions: () => [...softwareUpdatePackageKeys.all, 'definitions'] as const,
  rollouts: (id: string) => [...softwareUpdatePackageKeys.all, 'rollouts', id] as const,
};

export function useSoftwareUpdatePackages() {
  return useQuery({
    queryKey: softwareUpdatePackageKeys.list(),
    queryFn: () => softwareUpdatePackageService.list(),
  });
}

export function useSoftwareUpdatePackage(id: string) {
  return useQuery({
    queryKey: softwareUpdatePackageKeys.detail(id),
    queryFn: () => softwareUpdatePackageService.get(id),
    enabled: !!id,
  });
}

export function useSoftwareUpdatePackageDefinitions() {
  return useQuery({
    queryKey: softwareUpdatePackageKeys.definitions(),
    queryFn: () => softwareUpdatePackageService.listDefinitions(),
    // The device Overview fetches the definitions best-effort; viewers without
    // config:read get a 403 every time — don't burn a retry on auth errors.
    retry: (failureCount, err) => {
      if (isAxiosError(err) && (err.response?.status === 401 || err.response?.status === 403)) {
        return false;
      }
      return failureCount < 1;
    },
  });
}

export function useSoftwareUpdatePackageRollouts(id: string) {
  return useQuery({
    queryKey: softwareUpdatePackageKeys.rollouts(id),
    queryFn: () => softwareUpdatePackageService.listRollouts(id),
    enabled: !!id,
  });
}

export function useCreateSoftwareUpdatePackage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreatePackageRequest) => softwareUpdatePackageService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: softwareUpdatePackageKeys.lists() });
    },
  });
}

export function useUpdateSoftwareUpdatePackage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdatePackageRequest }) =>
      softwareUpdatePackageService.update(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: softwareUpdatePackageKeys.lists() });
      queryClient.invalidateQueries({ queryKey: softwareUpdatePackageKeys.detail(variables.id) });
    },
  });
}

export function useDeleteSoftwareUpdatePackage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => softwareUpdatePackageService.delete(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: softwareUpdatePackageKeys.lists() });
      // Soft delete keeps the row reachable (e.g. from archived rollouts); the
      // detail query must refetch so it shows the deleted state instead of a
      // stale pre-deletion copy (global staleTime is 5m).
      queryClient.invalidateQueries({ queryKey: softwareUpdatePackageKeys.detail(id) });
    },
  });
}

export type { Package, PackageDefinition, CreatePackageRequest, UpdatePackageRequest, PackageRolloutsResponse };
